import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Flame, Eye, Loader2, Megaphone, Check } from 'lucide-react';
import { getCoverThumbUrl } from '../../utils/coverUrl';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const QuarterlyChampionsPanel = () => {
  const { toast } = useToast();
  const [quarters, setQuarters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [announcing, setAnnouncing] = useState(false);
  const [announced, setAnnounced] = useState(false);

  const announceChampion = async () => {
    setAnnouncing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/trending-quarters/announce`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data || {};
      if (d.status === 'announced') {
        setAnnounced(true);
        toast({
          title: `${d.quarter} champion posted to Community Chat!`,
          description: `"${d.title}" (${(d.views || 0).toLocaleString()} views) is now celebrated in the general room.`,
        });
      } else if (d.status === 'already_announced') {
        setAnnounced(true);
        toast({
          title: `${d.quarter} champion was already posted`,
          description: 'Each quarter is only announced once.',
        });
      } else {
        toast({ title: 'No view data for last quarter', description: 'Nothing to announce yet.' });
      }
    } catch (e) {
      toast({ title: 'Could not post announcement', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setAnnouncing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/trending-quarters?quarters=4`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setQuarters(res.data?.quarters || []);
      } catch { /* panel is informational */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="mt-8 bg-white/5 rounded-xl border border-white/10 p-6" data-testid="quarterly-champions-panel">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div className="flex items-center">
          <Trophy className="h-6 w-6 text-amber-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Quarterly Champions</h2>
        </div>
        <button
          type="button"
          onClick={announceChampion}
          disabled={announcing}
          className="inline-flex items-center gap-1.5 text-sm font-medium bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 rounded-full px-4 py-2 transition-colors disabled:opacity-50"
          data-testid="announce-champion-btn"
        >
          {announcing ? <Loader2 className="h-4 w-4 animate-spin" /> : announced ? <Check className="h-4 w-4" /> : <Megaphone className="h-4 w-4" />}
          {announced ? 'Posted to Chat' : "Post Last Quarter's Champion to Chat"}
        </button>
      </div>
      <p className="text-sm text-gray-400 mb-5">
        Most-viewed books per calendar quarter. Trending Now resets on these same boundaries (Jan / Apr / Jul / Oct 1).
        The chat announcement is manual — click the button above when you want to celebrate last quarter's winner.
      </p>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
        </div>
      ) : quarters.length === 0 ? (
        <p className="text-sm text-gray-500 py-4" data-testid="quarterly-champions-empty">No view data yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {quarters.map((q) => (
            <div
              key={q.quarter}
              className={`rounded-lg border p-4 ${q.is_current ? 'border-amber-500/40 bg-amber-500/5' : 'border-white/10 bg-black/20'}`}
              data-testid={`quarter-card-${q.q}-${q.year}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-white">{q.quarter}</span>
                {q.is_current && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded-full px-2 py-0.5 flex items-center gap-1">
                    <Flame className="h-3 w-3" /> Current
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {q.top_books.map((b, idx) => (
                  <div key={b.book_id} className="flex items-center gap-2.5" data-testid={`quarter-book-${q.q}-${q.year}-${idx}`}>
                    <span className={`text-xs font-bold w-4 text-right ${idx === 0 ? 'text-amber-400' : 'text-gray-500'}`}>
                      {idx + 1}
                    </span>
                    {b.cover_image_url ? (
                      <img
                        src={getCoverThumbUrl(b.cover_image_url)}
                        alt=""
                        className="w-7 h-10 rounded object-cover border border-white/10 flex-shrink-0"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-7 h-10 rounded bg-white/10 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs truncate ${idx === 0 ? 'text-white font-semibold' : 'text-gray-300'}`}>
                        {b.title}
                      </p>
                      <p className="text-[10px] text-gray-500 truncate">{b.author}</p>
                    </div>
                    <span className="text-[11px] text-gray-400 flex items-center gap-1 flex-shrink-0">
                      <Eye className="h-3 w-3" /> {b.views.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuarterlyChampionsPanel;
